import React, { useState, useEffect } from 'react';
import { useAction } from 'convex/react';
import { api } from '@catchup/convex/convex/_generated/api';

/**
 * ConfirmDecline Component Props
 * Epic 4: Story 4.7 - Guest Final Confirmation & Decline
 */
export interface ConfirmDeclineProps {
  token: string;
  event: {
    _id: string;
    title: string;
    organizerName: string;
    status: string;
    confirmedDate?: number;
    duration?: string;
    location?: { name: string; address: string };
    meetingFormat?: string;
  };
  participant: {
    _id: string;
    displayName: string;
    status?: string;
  };
}

/**
 * ConfirmDecline Component
 *
 * Allows guests to confirm or decline attendance for a finalized event.
 * Displayed when event.status === 'confirmed' (organizer has picked final time).
 *
 * Features:
 * - Shows finalized date/time prominently
 * - Two action buttons: Confirm (coral) and Decline (gray)
 * - Success states with appropriate messages
 * - Already-responded state with option to change response
 * - Error handling with retry capability
 * - Optimistic UI updates with rollback on failure
 *
 * Story: 4.7 - Guest Final Confirmation & Decline
 */
export const ConfirmDecline: React.FC<ConfirmDeclineProps> = ({
  token,
  event,
  participant,
}) => {
  // State management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasResponded, setHasResponded] = useState(
    participant.status === 'confirmed' || participant.status === 'declined'
  );
  const [currentStatus, setCurrentStatus] = useState<'confirmed' | 'declined' | null>(
    participant.status === 'confirmed'
      ? 'confirmed'
      : participant.status === 'declined'
      ? 'declined'
      : null
  );
  const [error, setError] = useState<string | null>(null);
  const [showChangeResponse, setShowChangeResponse] = useState(false);

  // Action hook - uses Node.js runtime for JWT validation
  const confirmOrDeclineAction = useAction(api.participantActions.confirmOrDecline);

  // Check participant status on mount and prop changes
  useEffect(() => {
    const responded = participant.status === 'confirmed' || participant.status === 'declined';
    setHasResponded(responded);
    setCurrentStatus(
      participant.status === 'confirmed'
        ? 'confirmed'
        : participant.status === 'declined'
        ? 'declined'
        : null
    );
  }, [participant.status]);

  /**
   * Handle confirm action
   */
  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await confirmOrDeclineAction({ token, action: 'confirm' });

      if (result.success) {
        setHasResponded(true);
        setCurrentStatus('confirmed');
        setShowChangeResponse(false);
      } else {
        setError(result.error || 'Failed to confirm attendance');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle decline action
   */
  const handleDecline = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await confirmOrDeclineAction({ token, action: 'decline' });

      if (result.success) {
        setHasResponded(true);
        setCurrentStatus('declined');
        setShowChangeResponse(false);
      } else {
        setError(result.error || 'Failed to decline attendance');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Format confirmed date/time for display
   */
  const formatConfirmedDateTime = () => {
    if (!event.confirmedDate) return 'Date TBD';

    const date = new Date(event.confirmedDate);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    };

    return date.toLocaleString('en-US', options);
  };

  /**
   * Get meeting location text
   */
  const getMeetingLocationText = () => {
    if (!event.meetingFormat) return null;

    if (event.meetingFormat === 'virtual') {
      return 'Virtual';
    }

    if (event.meetingFormat === 'undecided') {
      return "We'll decide later";
    }

    // in_person
    return (
      <>
        <div className="font-medium">In person</div>
        {event.location?.name && <div>{event.location.name}</div>}
        {event.location?.address && (
          <div className="text-slate-600 text-base">{event.location.address}</div>
        )}
      </>
    );
  };

  // Already responded - show current status
  if (hasResponded && !showChangeResponse) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 p-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2 line-clamp-2">
            {event.title}
          </h1>
          <p className="text-lg text-slate-600">
            {event.organizerName} wants to catch up!
          </p>
        </div>

        {/* Confirmed Date/Time */}
        <div className="flex-1 p-6 space-y-6">
          <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
            <div className="text-sm font-medium text-slate-500 mb-2">When</div>
            <div className="text-xl font-bold text-slate-900">
              {formatConfirmedDateTime()}
            </div>
            {event.duration && (
              <div className="text-base text-slate-600 mt-1">{event.duration}</div>
            )}
          </div>

          {/* Where */}
          {event.meetingFormat && (
            <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
              <div className="text-sm font-medium text-slate-500 mb-2">Where</div>
              <div className="text-lg text-slate-900">{getMeetingLocationText()}</div>
            </div>
          )}

          {/* Current Status */}
          <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-center mb-4">
              <span
                className="material-symbols-outlined text-6xl"
                style={{
                  color: currentStatus === 'confirmed' ? '#FF6B6B' : '#94A3B8',
                  fontVariationSettings: '"FILL" 1, "wght" 600',
                }}
              >
                {currentStatus === 'confirmed' ? 'check_circle' : 'cancel'}
              </span>
            </div>

            <p className="text-xl font-bold text-center text-slate-900 mb-2">
              {currentStatus === 'confirmed'
                ? `You're confirmed for ${formatConfirmedDateTime()}`
                : 'You declined this hangout'}
            </p>

            <p className="text-base text-center text-slate-600 mb-6">
              {currentStatus === 'confirmed'
                ? "We'll see you there!"
                : 'Thanks for letting us know.'}
            </p>

            <button
              onClick={() => setShowChangeResponse(true)}
              className="w-full py-3 px-6 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
              aria-label="Change your response"
            >
              Change response
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show confirmation/decline UI
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 p-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-2 line-clamp-2">
          {event.title}
        </h1>
        <p className="text-lg text-slate-600">
          {event.organizerName} wants to catch up!
        </p>
      </div>

      {/* Details */}
      <div className="flex-1 p-6 space-y-6">
        {/* Confirmed Date/Time */}
        <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
          <div className="text-sm font-medium text-slate-500 mb-2">When</div>
          <div className="text-xl font-bold text-slate-900">
            {formatConfirmedDateTime()}
          </div>
          {event.duration && (
            <div className="text-base text-slate-600 mt-1">{event.duration}</div>
          )}
        </div>

        {/* Where */}
        {event.meetingFormat && (
          <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
            <div className="text-sm font-medium text-slate-500 mb-2">Where</div>
            <div className="text-lg text-slate-900">{getMeetingLocationText()}</div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-medium">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-600 underline text-sm mt-2 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Instruction */}
        <div className="text-center py-4">
          <p className="text-lg font-semibold text-slate-700">
            {hasResponded ? 'Change your response:' : 'Can you make it?'}
          </p>
        </div>
      </div>

      {/* Fixed Action Buttons */}
      <div className="p-6 bg-white border-t border-slate-200 space-y-3">
        <button
          onClick={handleConfirm}
          disabled={isSubmitting}
          className="w-full bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          aria-label="Confirm attendance"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Confirming...</span>
            </>
          ) : (
            "I'll be there"
          )}
        </button>

        <button
          onClick={handleDecline}
          disabled={isSubmitting}
          className="w-full bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-4 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          aria-label="Decline attendance"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-gray-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Updating...</span>
            </>
          ) : (
            "Can't make it"
          )}
        </button>
      </div>
    </div>
  );
};
